
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  orderBy,
  limit,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import Header from "./components/Header";
import useOnlineStatus from "./Presence";
import CountUp from "react-countup";
import BoostButton from "./components/BoostButton";

const InvestorDashboard = () => {
  useOnlineStatus();

  const [userData, setUserData] = useState(null);
  const [availability, setAvailability] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const [prevWalletBalance, setPrevWalletBalance] = useState(0);
  const [remainingTime, setRemainingTime] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [campaignProgress, setCampaignProgress] = useState(0);
  const [campaignInvalid, setCampaignInvalid] = useState(false);

  const countdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let unsubscribeUserSnapshot;
    let unsubscribeAuth;

    // Manage contacts listeners and per-chat listeners
    let unsubscribeContactsFounder;
    let unsubscribeContactsInvestor;
    const chatSubs = new Map(); // chatId -> { unRead, unMsgs, lastReadAt, msgsDocs }
    const unreadByChat = {}; // chatId -> number

    const computeTotalUnread = () => {
      const total = Object.values(unreadByChat).reduce((sum, n) => sum + (n || 0), 0);
      setUnreadCount(total);
    };

    const ensureChatSubscriptions = (uid, currentChatIds) => {
      // Remove listeners for chats that no longer exist
      for (const existingId of Array.from(chatSubs.keys())) {
        if (!currentChatIds.has(existingId)) {
          const subs = chatSubs.get(existingId);
          try { subs.unRead && subs.unRead(); } catch {}
          try { subs.unMsgs && subs.unMsgs(); } catch {}
          chatSubs.delete(existingId);
          delete unreadByChat[existingId];
        }
      }

      // Add listeners for new chats
      currentChatIds.forEach((chatId) => {
        if (chatSubs.has(chatId)) return;

        const readsRef = doc(db, "chats", chatId, "reads", uid);
        const msgsRef = collection(db, "chats", chatId, "messages");

        const state = {
          lastReadAt: null,
          msgsDocs: [],
          unRead: null,
          unMsgs: null,
        };

        const recomputeForChat = () => {
          const lra = state.lastReadAt;
          const lraDate = lra?.toDate ? lra.toDate() : lra || null;

          let count = 0;
          for (const d of state.msgsDocs) {
            const data = d.data();
            // Count only messages addressed to the current user and newer than lastReadAt
            if (data?.to !== uid) continue;
            const ts = data?.createdAt?.toDate ? data.createdAt.toDate() : null;
            if (!ts) continue;
            if (!lraDate || ts > lraDate) count += 1;
          }

          unreadByChat[chatId] = count;
          computeTotalUnread();
        };

        state.unRead = onSnapshot(readsRef, (docSnap) => {
          state.lastReadAt = docSnap.exists() ? docSnap.data()?.lastReadAt || null : null;
          recomputeForChat();
        });

        state.unMsgs = onSnapshot(
          query(msgsRef, orderBy("createdAt", "desc"), limit(50)),
          (snap) => {
            state.msgsDocs = snap.docs;
            recomputeForChat();
          }
        );

        chatSubs.set(chatId, state);
      });

      // After any add/remove, recompute total
      computeTotalUnread();
    };

    unsubscribeAuth = auth.onAuthStateChanged((user) => {
      // Cleanup any previous listeners when auth user changes
      if (unsubscribeUserSnapshot) { try { unsubscribeUserSnapshot(); } catch {} }
      if (unsubscribeContactsFounder) { try { unsubscribeContactsFounder(); } catch {} }
      if (unsubscribeContactsInvestor) { try { unsubscribeContactsInvestor(); } catch {} }
      for (const subs of chatSubs.values()) {
        try { subs.unRead && subs.unRead(); } catch {}
        try { subs.unMsgs && subs.unMsgs(); } catch {}
      }
      chatSubs.clear();
      for (const k in unreadByChat) delete unreadByChat[k];
      setUnreadCount(0);

      if (!user) {
        setLoadingAuth(false);
        setUserData(null);
        return;
      }

      const uid = user.uid;

      // User data listener
      const userRef = doc(db, "users", uid);
      unsubscribeUserSnapshot = onSnapshot(userRef, (docSnap) => {
        if (!docSnap.exists()) {
          setLoadingAuth(false);
          setUserData(null);
          return;
        }

        const data = docSnap.data();
        setUserData(data);
        setAvailability(data.isAvailableForDeals || false);

        // Animate wallet
        setPrevWalletBalance((prev) => {
          // use previous state to animate properly
          return walletBalance;
        });
        setWalletBalance(data.walletBalance || 0);

        // Campaign logic
        const campaignActive = data.campaignActive || false;
        let campaignStart = null;
        setCampaignInvalid(false);

        try {
          if (data.campaignStart?.toDate) campaignStart = data.campaignStart.toDate();
          else if (data.campaignStart instanceof Date) campaignStart = data.campaignStart;
          else if (campaignActive) setCampaignInvalid(true);
        } catch (err) {
          console.error("Invalid campaignStart:", err);
          campaignStart = null;
          if (campaignActive) setCampaignInvalid(true);
        }

        const campaignEnd = campaignStart
          ? new Date(campaignStart.getTime() + 180 * 24 * 60 * 60 * 1000)
          : null;

        if (countdownRef.current) clearInterval(countdownRef.current);

        if (campaignActive && campaignStart && campaignEnd) {
          const updateCountdown = () => {
            const now = new Date();
            const totalMs = Math.max(0, campaignEnd - now);

            const days = Math.floor(totalMs / (1000 * 60 * 60 * 24));
            const hours = Math.floor(
              (totalMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
            );
            const minutes = Math.floor(
              (totalMs % (1000 * 60 * 60)) / (1000 * 60)
            );
            const seconds = Math.floor((totalMs % (1000 * 60)) / 1000);

            setRemainingTime({ days, hours, minutes, seconds });
            setCampaignProgress(
              ((180 * 24 * 60 * 60 - totalMs / 1000) / (180 * 24 * 60 * 60)) * 100
            );
          };

          updateCountdown();
          countdownRef.current = setInterval(updateCountdown, 1000);
        } else {
          setRemainingTime({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          setCampaignProgress(0);
        }

        setLoadingAuth(false);
      });

      // Real-time unread inbox count across all chats for this user
      const contactsRef = collection(db, "contacts");
      const qFounder = query(contactsRef, where("founderId", "==", uid));
      const qInvestor = query(contactsRef, where("investorId", "==", uid));

      let founderDocs = [];
      let investorDocs = [];

      const rebuildChats = () => {
        // Collect current chatIds from contacts
        const setChatIds = new Set();
        const allDocs = [...founderDocs, ...investorDocs];

        allDocs.forEach((d) => {
          const data = d.data();
          const otherId = data.founderId === uid ? data.investorId : data.founderId;
          if (!otherId) return;
          const cId = [uid, otherId].sort().join("_");
          setChatIds.add(cId);
        });

        ensureChatSubscriptions(uid, setChatIds);
      };

      unsubscribeContactsFounder = onSnapshot(qFounder, (snap) => {
        founderDocs = snap.docs || [];
        rebuildChats();
      });

      unsubscribeContactsInvestor = onSnapshot(qInvestor, (snap) => {
        investorDocs = snap.docs || [];
        rebuildChats();
      });
    });

    return () => {
      try { unsubscribeAuth && unsubscribeAuth(); } catch {}
      try { unsubscribeUserSnapshot && unsubscribeUserSnapshot(); } catch {}
      try { unsubscribeContactsFounder && unsubscribeContactsFounder(); } catch {}
      try { unsubscribeContactsInvestor && unsubscribeContactsInvestor(); } catch {}
      for (const subs of chatSubs.values()) {
        try { subs.unRead && subs.unRead(); } catch {}
        try { subs.unMsgs && subs.unMsgs(); } catch {}
      }
      chatSubs.clear();
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []); // no walletBalance dependency

  const toggleAvailability = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const newAvailability = !availability;
    setAvailability(newAvailability);

    await updateDoc(doc(db, "users", uid), {
      isAvailableForDeals: newAvailability,
      lastUpdated: serverTimestamp(),
    });
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate("/");
  };

  if (loadingAuth)
    return (
      <div className="flex justify-center items-center h-screen">⏳ Loading...</div>
    );
  if (!userData)
    return (
      <div className="flex justify-center items-center h-screen text-gray-500">
        ⚠️ Not logged in
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 relative">
      <Header userData={userData} />

      {/* Investor Profile */}
      <div className="bg-white shadow-md rounded-2xl p-6 mb-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-4">👤 My Investor Profile</h2>
        <div className="space-y-2 text-gray-700">
          <p><strong>Full Name:</strong> {userData.fullName || "Not set"}</p>
          <p><strong>Business Name:</strong> {userData.businessName || "Not set"}</p>
          <p><strong>Location:</strong> {userData.location || "Not set"}</p>
          <p><strong>Industry:</strong> {userData.industry || "Not set"}</p>
          <p><strong>💰 Investment Range:</strong> {userData.investmentRange || "Not set"}</p>
        </div>
      </div>

      {/* Campaign Timer */}
      {userData.campaignActive && (
        <div className="bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 shadow-md rounded-2xl p-6 mb-6 border border-gray-200 text-white text-center">
          <h2 className="text-xl font-semibold mb-2">🔥 Campaign Timer</h2>
          {campaignInvalid && (
            <p className="text-yellow-100 font-semibold mb-2">
              ⚠️ Campaign start date seems invalid. Please contact support.
            </p>
          )}
          <p className="text-2xl font-bold mb-2">
            {remainingTime.days}d {remainingTime.hours}h {remainingTime.minutes}m{" "}
            {remainingTime.seconds}s
          </p>
          <div className="w-full bg-white bg-opacity-30 rounded-full h-4 overflow-hidden">
            <div
              style={{
                width: `${campaignProgress}%`,
                height: "100%",
                borderRadius: "9999px",
                transition: "width 0.5s",
                background: "linear-gradient(90deg, #fff, #ffeb3b, #f44336)",
              }}
            />
          </div>
        </div>
      )}

      {/* Deal Availability */}
      <div className="bg-white shadow-md rounded-2xl p-6 mb-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-2">🎯 Deal Availability</h2>
        <p className="text-gray-700 mb-4">
          {availability
            ? "🟢 You’re currently open to deals"
            : "🔴 You’re not looking for a deal right now"}
        </p>
        <button
          onClick={toggleAvailability}
          className={`px-4 py-2 rounded-xl font-medium transition ${
            availability
              ? "bg-red-500 text-white hover:bg-red-600"
              : "bg-green-600 text-white hover:bg-green-700"
          }`}
        >
          {availability ? "Mark as Unavailable" : "Mark as Available"}
        </button>
      </div>

      {/* Boost */}
      <div className="bg-yellow-50 shadow-md rounded-2xl p-6 mb-6 border border-yellow-300">
        <h2 className="text-xl font-semibold mb-2"> 🔥Investor Boost</h2>
        <p className="text-gray-700 mb-4">
          Activate a 7-day premium boost to get more attention from entrepreneurs.
        </p>
        {/* Reusable Boost Button */}
        <BoostButton userId={auth.currentUser?.uid} />
      </div>

      {/* Earnings */}
      <div className="bg-white shadow-md rounded-2xl p-6 mb-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-2">💰 Earnings</h2>
        {userData.campaignActive ? (
          <>
            <p className="text-2xl font-bold text-green-700 mb-2">
              {userData?.walletCurrency || "R"}
              <CountUp
                start={prevWalletBalance}
                end={walletBalance}
                duration={1.5}
                separator=","
              />{" "}
              earned
            </p>
            <p className="text-gray-600">
              ✅ Incentive is credited <strong>once per entrepreneur</strong>{" "}
              after the campaign starts. 🏦 Payouts are released after 180 days. 💼
            </p>
          </>
        ) : (
          <p className="text-gray-600 italic">
            💼 Your campaign hasn’t started yet — earnings will show once it
            begins.
          </p>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="flex justify-center gap-4 mt-10">
        <button
          onClick={() => navigate("/inbox")}
          className={`bg-blue-600 text-white px-4 py-2 rounded-2xl shadow hover:bg-blue-700 transition flex items-center gap-2 ${
            unreadCount > 0 ? "animate-pulse" : ""
          }`}
        >
          <span>💬 Inbox</span>
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs">
              {unreadCount}
            </span>
          )}
        </button>
        <button
          onClick={() => navigate("/edit-investor-profile")}
          className="bg-yellow-500 text-white px-4 py-2 rounded-2xl shadow hover:bg-yellow-600 transition"
        >
          ✏️ Edit Profile
        </button>
        <button
          onClick={handleLogout}
          className="bg-gray-700 text-white px-4 py-2 rounded-2xl shadow hover:bg-gray-800 transition"
        >
          🔴 Logout
        </button>
      </div>
    </div>
  );
};

export default InvestorDashboard;